import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { DataGridCardProps, DataGridProps } from "designSystem/components/dataGrid/DataGrid";
import TextField from "designSystem/components/textfield/TextField";
import { Box, DataGrid, FlexColumn, FlexRow, PopperInContainer, Tab, TabPanel, Tabs, Text } from "designSystem/index";
import { ILocaleProvider, useLocale } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { ComponentItemDataStore } from "designSystem/utilities/component-item-functions";
import { debounce } from "lodash";
import { action, makeObservable, observable, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { ChangeEvent, FunctionComponent, SyntheticEvent, createContext, useContext, useEffect, useMemo, useState } from "react";
import { GridColumnProps } from "../grid/GridContainer";
import SelectListItem, { SelectListItemProps } from "../list/SelectListItem";
import SearchPlaceholder from "../placeholder/SearchPlaceholder";
import { onSearchComponentItemDataStore } from "./SearchSingleSelect";

export const DefaultSingleSelectWithTabCard: FunctionComponent = (props: DataGridCardProps<ComponentItem>) => {
	const { item } = props;
	const loc = useLocale();
	const text = loc.getComponentItemName(item);

	return (
		<Box>
			<Text text={text} />
		</Box>
	);
};

export interface DropdownTab<T> {
	/** properties to apply to the data grid containing the results */
	dataGridProps: Omit<DataGridProps<T>, "columns" | "columnsByWidth">;

	/** properties to apply to each list item */
	listItemProps?: SelectListItemProps;

	/** name of the tab */
	nameRes: string;
}

interface SingleSelectWithTabsProps<T> extends Omit<GridColumnProps, "onSelect" | "placeholder"> {
	/** callback called when an item is selected or when blurred/escaped with no value */
	onSelect?: (item?: T) => void;

	/** localization resource to use for placeholder */
	placeholderRes?: string;

	/** parameters to use for placeholder resource */
	placeholderResParams?: Array<number | string>;

	/** tabs to display */
	tabs: DropdownTab<T>[];

	/** selected value */
	value?: ComponentItem;
}

const SingleSelectWithTabs = observer(function SingleSelectWIthTabs<T extends ComponentItem>(props: SingleSelectWithTabsProps<T>) {
	const loc = useLocale();
	const [store, setStore] = useState<SingleSelectWithTabsStore<T>>(new SingleSelectWithTabsStore(props, loc));

	// reload the store once the tab changes
	useEffect(() => {
		setStore(new SingleSelectWithTabsStore(props, loc));
	}, [props.tabs]);

	const {
		anchorEl,
		clickAway,
		handleChange,
		onChangeText,
		onClickTextBox,
		otherProps,
		props: { placeholderRes, placeholderResParams },
		searchText,
		selectedIndex,
		tabNames,
		tabs,
	} = store;

	return (
		<SingleSelectWithTabsStoreContext.Provider value={store}>
			<ClickAwayListener onClickAway={clickAway}>
				<FlexColumn {...otherProps}>
					<FlexRow>
						<TextField
							InputProps={{
								endAdornment: <ArrowDropDownIcon />,
							}}
							fullWidth
							onChange={onChangeText}
							onClick={onClickTextBox}
							placeholderRes={placeholderRes}
							placeholderResParams={placeholderResParams}
							sx={{
								width: 200,
							}}
							value={searchText}
						/>
					</FlexRow>
					<FlexRow>
						<PopperInContainer
							anchorEl={anchorEl}
							paperProps={{
								sx: {
									display: "flex",
									flexDirection: "column",
									maxHeight: "300px",
									overflow: "auto",
								},
							}}
						>
							<Tabs
								onChange={handleChange}
								sx={{
									paddingX: "5px",
								}}
								value={selectedIndex}
								variant="scrollable"
								visibleScrollbar
							>
								{tabNames.map((tab, index) => <Tab key={`tab${index}`} label={tab} />)}
							</Tabs>
							{tabs.map((tab, i) => (
								<SinglePanel
									index={i}
									key={`tab-panel-${i}`}
									tab={tab}
								/>
							))
							}
						</PopperInContainer>
					</FlexRow>
				</FlexColumn>
			</ClickAwayListener>
		</SingleSelectWithTabsStoreContext.Provider>
	);
});

interface SinglePanelProps<T> {
	/** index of each panel */
	index: number;

	/** tab instance */
	tab: DropdownTab<T>;
}

const SinglePanel: FunctionComponent<SinglePanelProps<ComponentItem>> = observer((props) => {
	const {
		index,
		tab: { dataGridProps, listItemProps },
	} = props;

	const {
		CardComponent,
		PlaceholderComponent,
		rowsPerPage,
		store,
		...otherGridProps
	} = dataGridProps;

	const {
		onSelect,
		searchStore,
		selectedIndex,
		selectedValue,
	} = useSingleSelectWithTabsStore();

	/** single item component */
	const ItemComponent = useMemo(() => {
		return function ItemComponent(props: DataGridCardProps<ComponentItem>): JSX.Element {
			const { item } = props;
			const { disabled, id } = item;

			return (
				<SelectListItem
					disabled={disabled}
					onClick={() => onSelect(item)}
					selected={selectedValue?.id === id}
					{...listItemProps}
				>
					<CardComponent {...props} />
				</SelectListItem>
			);
		};
	}, [selectedValue]);

	return (
		<TabPanel
			index={index}
			key={`TabPanel${index}`}
			overflow="auto"
			selectedTabIndex={selectedIndex}
		>
			<DataGrid
				CardComponent={ItemComponent}
				PlaceholderComponent={PlaceholderComponent || SearchPlaceholder}
				columns={1}
				rowsPerPage={rowsPerPage || 20}
				store={searchStore}
				{...otherGridProps}
			/>
		</TabPanel>
	);
});

class SingleSelectWithTabsStore<T extends ComponentItem> {
	readonly props: SingleSelectWithTabsProps<T>;
	readonly loc: ILocaleProvider;

	/** from the component’s perspective, the store is the source of truth, save the otherProps as part of the store */
	readonly otherProps: Omit<GridColumnProps, "onSelect">;

	/** ref for the popper */
	@observable anchorEl: HTMLElement = null;

	@observable.ref private _selectedValue: ComponentItem;

	/** selected index for the tab */
	@observable selectedIndex: number = 0;

	/** the text typed in the textbox for searching */
	@observable searchText: string = "";

	@observable.ref private _searchStore: ComponentItemDataStore<T>;


	/** the store of the current tab  */
	get searchStore(): ComponentItemDataStore<T> {
		const { selectedIndex, tabStores } = this;
		return this._searchStore || tabStores[selectedIndex];
	}

	/** select value(not the name/label) from the list */
	get selectedValue(): ComponentItem {
		return this._selectedValue;
	}

	/** tabs with the dropdown */
	get tabs(): DropdownTab<T>[] {
		return this.props.tabs;
	}

	/** tab names */
	get tabNames(): string[] {
		const { tabs, loc } = this;
		return tabs.map((tab) => loc.resource(tab.nameRes));
	}

	/** the stores map attached to the tab */
	get tabStores(): Record<number, ComponentItemDataStore<T>> {
		const { tabs } = this;

		const stores = {};
		tabs.forEach((tab, index) => {
			stores[index] = tab.dataGridProps.store as ComponentItemDataStore<T>;
		});

		return stores;
	}

	/** handle the click away event to close the tabs container */
	@action clickAway = (): void => {
		const { loc, selectedValue } = this;
		this.anchorEl = null;

		// make the search text in the textbox match the actual item if user modified it
		this.searchText = !!selectedValue ? loc.getComponentItemName(selectedValue) : "";
	};

	/** handle the logic when switch tabs */
	@action handleChange = (event: SyntheticEvent<Element, Event>, newValue: number): void => {
		this.selectedIndex = newValue;
		this.triggerSearch(this.searchText);
	};

	/** callback when user types in something */
	@action onChangeText = async (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): Promise<void> => {
		const value = event.target.value;
		this.searchText = value;
		this.debouncedTriggerSearch(value);
	};

	/** callback when user selects something */
	@action onSelect = (item: T): void => {
		const { clickAway, loc, props: { onSelect } } = this;
		onSelect?.(item);
		this._selectedValue = item;
		this.searchText = loc.getComponentItemName(item);
		clickAway();
	};

	/** callback for click, open the tabs container */
	@action onClickTextBox = (event): void => {
		this.anchorEl ||= event.currentTarget;

		// handle the case when user filters down the results then click away without selecting anything
		if (!this.searchText) {
			this._searchStore = null;
		}
	};

	/** trigger the search logic */
	@action triggerSearch = async (searchText: string): Promise<void> => {
		const { loc, selectedIndex, tabStores } = this;
		const currentStore = tabStores[selectedIndex];
		const filteredStore = await onSearchComponentItemDataStore(currentStore, searchText, loc);

		runInAction(() => {
			this._searchStore = filteredStore;
		});
	};

	/** debounced version of the search  */
	private debouncedTriggerSearch = (searchText: string): void => {
		debounce(() => this.triggerSearch(searchText), 200)();
	};

	constructor(props: SingleSelectWithTabsProps<T>, loc: ILocaleProvider) {
		makeObservable(this);
		this.loc = loc;
		this.props = props;

		const {
			itemType,
			onSelect,
			placeholderRes,
			placeholderResParams,
			tabs,
			value,
			...otherProps
		} = props;

		this.otherProps = otherProps;
		this._selectedValue = value;
	}
}

const SingleSelectWithTabsStoreContext = createContext<SingleSelectWithTabsStore<ComponentItem>>(undefined);
const useSingleSelectWithTabsStore = () => useContext(SingleSelectWithTabsStoreContext);

export default SingleSelectWithTabs;